import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

export default function GuidedTour({ steps, onComplete, isActive }) {
  const { i18n } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [tooltipPlacement, setTooltipPlacement] = useState('bottom');
  const lang = i18n.language || 'es';

  const buttonText = {
    es: { skip: 'Saltar tour', next: 'Siguiente', prev: 'Anterior', finish: '¡Listo!' },
    en: { skip: 'Skip tour', next: 'Next', prev: 'Previous', finish: 'Done!' },
    gl: { skip: 'Saltar tour', next: 'Seguinte', prev: 'Anterior', finish: '¡Listo!' },
    ca: { skip: 'Saltar tour', next: 'Següent', prev: 'Anterior', finish: 'Fet!' },
    zh: { skip: '跳过导览', next: '下一步', prev: '上一步', finish: '完成！' }
  };
  const btn = buttonText[lang] || buttonText.es;

  const calculatePosition = useCallback(() => {
    if (!steps || steps.length === 0 || !isActive) return;
    
    const step = steps[currentStep];
    if (!step?.target) return;

    const element = document.querySelector(step.target);
    if (!element) {
      console.log(`Tour: Element not found for selector "${step.target}"`);
      return;
    }

    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Highlight the element
    element.style.position = 'relative';
    element.style.zIndex = '1001';
    element.style.boxShadow = '0 0 0 4px rgba(255, 139, 167, 0.5), 0 0 0 9999px rgba(0,0,0,0.5)';
    element.style.borderRadius = '12px';

    // Calculate best position for tooltip
    const tooltipHeight = 200;
    const tooltipWidth = 320;
    const margin = 16;

    let placement = step.placement || 'bottom';
    let top, left;

    // Auto-adjust placement if not enough space
    if (placement === 'bottom' && rect.bottom + tooltipHeight + margin > viewportHeight) {
      placement = 'top';
    } else if (placement === 'top' && rect.top - tooltipHeight - margin < 0) {
      placement = 'bottom';
    }

    switch (placement) {
      case 'top':
        top = rect.top - tooltipHeight - margin + window.scrollY;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'bottom':
        top = rect.bottom + margin + window.scrollY;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
        break;
      case 'left':
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2) + window.scrollY;
        left = rect.left - tooltipWidth - margin;
        break;
      case 'right':
        top = rect.top + (rect.height / 2) - (tooltipHeight / 2) + window.scrollY;
        left = rect.right + margin;
        break;
      default:
        top = rect.bottom + margin + window.scrollY;
        left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
    }

    // Ensure tooltip stays within viewport
    left = Math.max(margin, Math.min(left, viewportWidth - tooltipWidth - margin));

    setTooltipPosition({ top, left });
    setTooltipPlacement(placement);

    // Scroll element into view if needed
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [currentStep, steps, isActive]);

  useEffect(() => {
    if (!isActive) return;
    
    calculatePosition();
    
    // Recalculate on resize
    window.addEventListener('resize', calculatePosition);
    return () => window.removeEventListener('resize', calculatePosition);
  }, [calculatePosition, isActive]);

  // Clean up highlights when changing steps
  useEffect(() => {
    return () => {
      // Remove highlight from all elements
      document.querySelectorAll('[style*="z-index: 1001"]').forEach(el => {
        el.style.position = '';
        el.style.zIndex = '';
        el.style.boxShadow = '';
      });
    };
  }, [currentStep]);

  // Final cleanup when tour ends
  useEffect(() => {
    return () => {
      document.querySelectorAll('[style*="z-index: 1001"]').forEach(el => {
        el.style.position = '';
        el.style.zIndex = '';
        el.style.boxShadow = '';
      });
    };
  }, []);

  if (!isActive || !steps || steps.length === 0) return null;

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const nextStep = () => {
    // Clean current highlight
    const currentElement = document.querySelector(step.target);
    if (currentElement) {
      currentElement.style.position = '';
      currentElement.style.zIndex = '';
      currentElement.style.boxShadow = '';
    }

    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    // Clean current highlight
    const currentElement = document.querySelector(step.target);
    if (currentElement) {
      currentElement.style.position = '';
      currentElement.style.zIndex = '';
      currentElement.style.boxShadow = '';
    }

    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipTour = () => {
    // Clean all highlights
    document.querySelectorAll('[style*="z-index: 1001"]').forEach(el => {
      el.style.position = '';
      el.style.zIndex = '';
      el.style.boxShadow = '';
    });
    onComplete();
  };

  // Arrow styles based on placement
  const arrowStyles = {
    top: 'bottom-[-8px] left-1/2 -translate-x-1/2 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white',
    bottom: 'top-[-8px] left-1/2 -translate-x-1/2 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white',
    left: 'right-[-8px] top-1/2 -translate-y-1/2 border-t-8 border-b-8 border-l-8 border-t-transparent border-b-transparent border-l-white',
    right: 'left-[-8px] top-1/2 -translate-y-1/2 border-t-8 border-b-8 border-r-8 border-t-transparent border-b-transparent border-r-white'
  };

  return (
    <>
      {/* Overlay - click to skip */}
      <div 
        className="fixed inset-0 z-[1000]"
        onClick={skipTour}
        data-testid="tour-overlay"
      />

      {/* Tooltip */}
      <div
        className="fixed z-[1002] w-80 animate-in fade-in slide-in-from-bottom-2 duration-300"
        style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        data-testid="tour-tooltip"
      >
        <div className="bg-white dark:bg-zinc-800 border-2 border-[#18181B] dark:border-zinc-600 rounded-xl shadow-lg p-4 relative">
          {/* Arrow */}
          <div className={`absolute w-0 h-0 ${arrowStyles[tooltipPlacement]}`} />

          {/* Header */}
          <div className="flex items-start justify-between mb-2">
            <span className="text-xs font-bold text-[#FF8BA7] uppercase tracking-wide">
              {currentStep + 1} / {steps.length}
            </span>
            <button
              onClick={skipTour}
              className="text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300 p-1 -mt-1 -mr-1"
              aria-label="Close tour"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Content */}
          <h3 className="font-bold text-lg mb-2 text-zinc-900 dark:text-white">{step.title}</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-4">{step.content}</p>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={skipTour}
              className="text-xs text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300"
            >
              {btn.skip}
            </button>
            <div className="flex gap-2">
              {!isFirstStep && (
                <button
                  onClick={prevStep}
                  className="rounded-full border-2 border-[#18181B] dark:border-zinc-600 bg-white dark:bg-zinc-700 p-2 hover:bg-zinc-50 dark:hover:bg-zinc-600 transition-all"
                >
                  <ChevronLeft className="w-4 h-4 text-zinc-900 dark:text-white" />
                </button>
              )}
              <button
                onClick={nextStep}
                className="rounded-full border-2 border-[#18181B] bg-[#18181B] text-white px-4 py-2 font-bold text-sm hover:bg-[#333] transition-all flex items-center gap-1"
                data-testid="tour-next-btn"
              >
                {isLastStep ? btn.finish : btn.next}
                {!isLastStep && <ChevronRight className="w-3 h-3" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
